import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, initials, fmtDate } from "@/lib/helpers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { GraduationCap, LogOut, Printer, Loader2, Paperclip, CalendarDays, NotebookPen } from "lucide-react";

const Stat = ({ label, value, tone = "slate" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div><div className={`text-xl font-heading font-bold mt-1 tabular text-${tone}-600`}>{value}</div></div>
);

export default function Portal() {
  const { user, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [selId, setSelId] = useState("");
  const [s, setS] = useState(null);
  const [homework, setHomework] = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    api.get("/my-students").then((r) => { setChildren(r.data); if (r.data[0]) setSelId(r.data[0].id); });
  }, []);
  useEffect(() => { if (selId) api.get(`/students/${selId}`).then((r) => setS(r.data)); }, [selId]);
  useEffect(() => { if (selId) api.get(`/my-homework/${selId}`).then((r) => setHomework(r.data || [])).catch(() => setHomework([])); }, [selId]);

  const openAttachment = async (h) => {
    try {
      const r = await api.get(`/homework/${h.id}/attachment`);
      const a = document.createElement("a");
      a.href = r.data.data; a.download = r.data.name || "attachment"; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* ignore */ }
  };
  const isOverdue = (d) => d && d < new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white"><GraduationCap size={20} /></div>
          <div><div className="font-heading font-extrabold text-lg leading-none">SSSPS</div><div className="text-[10px] uppercase tracking-widest text-slate-400">{user?.role} Portal</div></div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.name}</span>
          <button onClick={logout} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid="portal-logout"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
        {children.length > 1 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600">Select Child:</span>
            <Select value={selId} onValueChange={setSelId}><SelectTrigger className="w-56" data-testid="child-select"><SelectValue /></SelectTrigger>
              <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.class_name})</SelectItem>)}</SelectContent></Select>
          </div>
        )}

        {!s ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div> : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-2xl font-heading font-bold">{initials(s.name)}</div>
              <div className="flex-1"><h1 className="font-heading text-2xl font-extrabold text-slate-900">{s.name}</h1><p className="text-slate-500">{s.admission_number} · {s.class_name} {s.section} · Roll {s.roll_number}</p></div>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${s.fees_summary.due > 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>Due {fmt(s.fees_summary.due)}</div>
            </div>

            <Tabs defaultValue="fees">
              <TabsList className="flex-wrap h-auto"><TabsTrigger value="fees">Fees</TabsTrigger><TabsTrigger value="homework" data-testid="portal-tab-homework">Homework</TabsTrigger><TabsTrigger value="profile">Profile</TabsTrigger><TabsTrigger value="attendance">Attendance</TabsTrigger><TabsTrigger value="transport">Transport</TabsTrigger><TabsTrigger value="receipts">Receipts</TabsTrigger></TabsList>

              <TabsContent value="fees" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Stat label="Total" value={fmt(s.fees_summary.total_payable)} />
                  <Stat label="Paid" value={fmt(s.fees_summary.paid)} tone="emerald" />
                  <Stat label="Discount" value={fmt(s.fees_summary.discount)} tone="amber" />
                  <Stat label="Due" value={fmt(s.fees_summary.due)} tone="rose" />
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h4 className="font-semibold text-slate-800">Fee Breakdown (Annual)</h4>
                    <a href={`${API}/students/${selId}/fee-statement-pdf?token=${token}&download=1`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="gap-1.5" data-testid="portal-fee-statement-btn"><Printer size={14} />Download Fee Statement</Button>
                    </a>
                  </div>
                  {s.fees_summary.breakdown.map((b, i) => <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-50"><span className="text-slate-600">{b.fee_name} <span className="text-xs text-slate-400">({b.frequency})</span></span><span className="tabular font-medium">{fmt(b.amount)}</span></div>)}
                </div>
              </TabsContent>

              <TabsContent value="homework" className="mt-4 space-y-3" data-testid="portal-homework">
                {homework.length === 0 ? (
                  <div className="bg-white rounded-xl border border-slate-200 py-14 text-center text-slate-400">
                    <NotebookPen size={38} className="mx-auto mb-2 opacity-40" />No homework assigned yet.
                  </div>
                ) : homework.map((h) => (
                  <div key={h.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`portal-homework-${h.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {h.subject && <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 rounded px-2 py-0.5">{h.subject}</span>}
                      {h.due_date && <span className={`text-[11px] font-medium rounded px-2 py-0.5 ${isOverdue(h.due_date) ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>Due {fmtDate(h.due_date)}</span>}
                    </div>
                    <h4 className="font-semibold text-slate-800 mt-2">{h.title}</h4>
                    {h.description && <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{h.description}</p>}
                    <div className="flex items-center gap-4 text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex-wrap">
                      <span className="flex items-center gap-1"><CalendarDays size={13} />Assigned {h.assigned_date ? fmtDate(h.assigned_date) : "—"}</span>
                      {h.has_attachment && <button onClick={() => openAttachment(h)} className="flex items-center gap-1 text-indigo-600 hover:underline font-medium" data-testid={`portal-homework-attachment-${h.id}`}><Paperclip size={13} />Attachment</button>}
                    </div>
                  </div>
                ))}
              </TabsContent>


              <TabsContent value="profile" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-4 text-sm">
                  {[["Father", s.father_name], ["Mother", s.mother_name], ["DOB", s.dob], ["Gender", s.gender], ["Blood", s.blood_group], ["Phone", s.phone], ["Address", s.address], ["City", s.city], ["Session", s.academic_session]].map(([k, v]) => <div key={k}><div className="text-slate-400 text-xs uppercase">{k}</div><div className="font-medium text-slate-800">{v || "—"}</div></div>)}
                </div>
              </TabsContent>

              <TabsContent value="attendance" className="mt-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4"><Stat label="Present" value={s.attendance.present} tone="emerald" /><Stat label="Absent" value={s.attendance.absent} tone="rose" /><Stat label="Leave" value={s.attendance.leave} tone="amber" /><Stat label="Percentage" value={`${s.attendance.percentage}%`} tone="indigo" /></div>
              </TabsContent>

              <TabsContent value="transport" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm">
                  {s.transport ? <div className="grid sm:grid-cols-2 gap-4">{[["Route", s.transport.route], ["Pickup", s.transport.pickup_point], ["Vehicle", s.transport.vehicle], ["Fee", fmt(s.transport.transport_fee)]].map(([k, v]) => <div key={k}><div className="text-slate-400 text-xs uppercase">{k}</div><div className="font-medium text-slate-800">{v}</div></div>)}</div> : <p className="text-slate-400">No transport assigned.</p>}
                </div>
              </TabsContent>

              <TabsContent value="receipts" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {s.payments.filter((p) => p.status !== "Void").length === 0 ? <p className="text-slate-400 p-6">No receipts yet.</p> : (
                    <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-3">Receipt</th><th className="text-left px-4 py-3">Date</th><th className="text-right px-4 py-3">Amount</th><th className="px-4 py-3"></th></tr></thead>
                      <tbody className="divide-y divide-slate-100">{s.payments.filter((p) => p.status !== "Void").map((p) => <tr key={p.id}><td className="px-4 py-3 font-mono text-xs">{p.receipt_number}</td><td className="px-4 py-3">{fmtDate(p.payment_date)}</td><td className="px-4 py-3 text-right font-semibold tabular">{fmt(p.amount_paid)}</td><td className="px-4 py-3 text-right"><a href={`${API}/receipts/${p.id}/pdf?token=${token}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><Printer size={14} /></Button></a></td></tr>)}</tbody>
                    </table>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
